import React from 'react';
import useUnitData from '../hooks/UseUnitData';
import { useConfig } from '../contexts/ConfigProvider';
import useCostData from '../hooks/UseCostData';
import { createRental } from '../modules/api';
// import ContactUpdate from './ContactUpdate';
import MoveIn from '../components/MoveIn';
import { SET_IS_SUBMITTED } from '../components/MoveIn/reducer';
import { getMoveInDate, NO_LOCK_ID, selectedInsuranceObj } from '../modules/utils';
import { LOCAL_STORAGE_KEY } from '../contexts/FormProvider/FormProvider';
import { EVENTS_NAME } from '../modules/events';
import { detectCardType } from '../modules/cardUtils';

const propTypes = {};
const defaultProps = {};

const Submit = () => {
  const {
    data: {
      facility,
      reservation: {
        id: reservationId,
        contactId,
        alias: reservationAlias,
        contact: { alias: contactAlias },
      },
      unit,
    },
  } = useUnitData();
  const { config: { params }, triggerEvent } = useConfig();
  const { data: { dueToday, charges, totalTaxes } } = useCostData();
  const { timezone, geo, invoicePeriod, invoiceDay, insuranceOptions } = facility;

  const toBase64 = (file) => new Promise((resolve, reject) => {
    if (!file) {
      resolve();
    }

    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      resolve(fileReader.result);
    };

    fileReader.onerror = (error) => {
      reject(error);
    };
  });

  const onSubmit = async ({ formValues, dispatch, onStepChange }) => {
    let cType = '';
    if (formValues.paymentType === 'credit') {
      cType = detectCardType(formValues.ccNumber);
    } else if (formValues.paymentType === 'debit') {
      cType = detectCardType(formValues.dcNumber);
    }
    const providerId = facility.alias.split(':')[2];

    if (providerId === 'se' && cType === 'amex') {
      cType = 'american_express';
    }
    const [organizationId, facilityId] = facility.alias.split(':')[3].split(';');
    const image = await toBase64(formValues.dlImage[0]);

    const body = {
      contact: {
        driversLicense: formValues.driversLicense,
        email: formValues.email,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        mailing: formValues.mailing,
        militaryStatus: formValues.militaryStatus,
        phone: formValues.phone,
        image,
        alias: contactAlias,
      },
      billing: formValues.billing,
      paymentType: formValues.paymentType,
      saveCreditCard: formValues.saveCreditCard,
      autoPayEnabled: formValues.enrollAutoPay,
      lock: formValues.lock !== NO_LOCK_ID ? formValues.lock : null,
      moveInDate: getMoveInDate(formValues.moveInDate, timezone, geo),
      insurance: formValues.insurance,
      selectedInsurance: selectedInsuranceObj(insuranceOptions, formValues.insurance),
      payment: {
        amount: dueToday,
      },
      ccType: cType,
      // update once we have discounts from backend
      concessionId: -999,
      additionalContacts: formValues.contacts || [],
      contactId,
      contactAlias,
      providerId,
      organizationId,
      facilityId,
      emailInfo: {
        payments: { dueToday, charges, totalTaxes },
        facility: {
          address: facility.address,
          displayName: facility.displayName,
          emailAddress: facility.emailAddress,
          phoneNumber: facility.phoneNumber,
        },
        unit: {
          amenities: unit.amenities,
          dimensions: unit.dimensions,
          name: unit.name,
          rates: unit.rates,
        },
      },
      returnUrl: window.location.href,
      hasVehicle: formValues.hasVehicle === 'yes',
      military: formValues.militaryStatus === 'yes',
      invoicePeriod,
      invoiceDay,
    };

    if (params.source) {
      body.source = params.source;
    }
    if (params.config) {
      body.configName = params.config;
    }

    if (formValues.hasVehicle === 'yes') {
      // for siteLink
      if (providerId === 'sl') {
        // eslint-disable-next-line prefer-destructuring
        body.vehicle = formValues.vehicles[0];
      }
      // for storEDGE
      if (providerId === 'se') {
        body.storageItems = formValues.vehicles;
      }
    }
    if (formValues.paymentType === 'ach') {
      body.routingNumber = formValues.achRoutingNumber;
      body.achFirstName = formValues.achFirstName;
      body.achLastName = formValues.achLastName;
      body.accountNumber = formValues.achAccountNumber;
      body.accountType = formValues.achAccountType;
      body.bankName = formValues.achBankName;
    } else if (formValues.paymentType === 'credit') {
      body.ccNumber = formValues.ccNumber.replace(/[\s-]/g, '');
      body.ccExp = formValues.ccExp;
      body.ccCSC = formValues.ccCSC;
    } else if (formValues.paymentType === 'debit') {
      body.ccNumber = formValues.dcNumber.replace(/[\s-]/g, '');
      body.ccExp = formValues.dcExp;
      body.ccCSC = formValues.dcCSC;
    }

    if (formValues.lienholders[0]?.name && formValues.lienholders[0]?.address1
      && formValues.lienholders[0]?.city && formValues.lienholders[0]?.state
      && formValues.lienholders[0]?.zipCode) {
      body.lienHolders = body.lienHolders || {};
      body.lienHolders = formValues.lienholders;
    }
    createRental({ reservationId, body })
      .then((res) => res.json())
      .then((res) => {
        if (res.error) {
          const errors = res.details?.map((details) => details.errors).flat() || [res.error];
          dispatch({
            type: SET_IS_SUBMITTED,
            payload: { success: false, errors },
          });
          triggerEvent(EVENTS_NAME.ERROR, { action: 'submission', errors: errors.map((error) => ({ name: error })) });
        } else if (res.rental?.error) {
          dispatch({
            type: SET_IS_SUBMITTED,
            payload: { success: false, errors: [res.rental.message] },
          });
          triggerEvent(EVENTS_NAME.ERROR, { action: 'submission', errors: [{ name: res.rental.message }] });
        } else {
          dispatch({
            type: SET_IS_SUBMITTED,
            payload: {
              success: true,
              leaseUrl: res.rental.leaseUrl,
              rentalAlias: res.rental.rental.alias,
              rentalId: res.rental.rental.id,
              gateAccessCode: res.rental.rental.gateAccessCode,
            },
          });
          onStepChange?.();
          // Clear localStorage after submission
          localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${reservationAlias}`);
          triggerEvent(EVENTS_NAME.STEP_CHANGE, {
            navigatingTo: { identifier: 'paymentConfirmation' },
            navigatingFrom: { identifier: 'payment' },
            pageView: 'paymentConfirmation',
            action: 'Payment Complete',
          });
        }
      })
      .catch((e) => {
        dispatch({
          type: SET_IS_SUBMITTED,
          payload: { success: false, errors: [e.message] },
        });
        triggerEvent(EVENTS_NAME.ERROR, { action: 'submission', errors: [{ name: e.message }] });
      });
  };

  return <MoveIn onSubmit={onSubmit} />;
};

Submit.propTypes = propTypes;
Submit.defaultProps = defaultProps;

export default Submit;
